import {
  computed,
  getCurrentScope,
  inject,
  nextTick,
  onScopeDispose,
  provide,
  readonly,
  ref,
  type Ref,
} from 'vue';
import debug from 'debug';
import type {
  LoadReporter,
  PageLoadState,
  PageLoadTracker,
  PageLoadTrackerOptions,
} from './types';

const log = debug('itp:core:pageLoadTracker');

export const PAGE_LOAD_TRACKER_KEY = 'itp-page-load-tracker';

const DEFAULT_TIMEOUT_MS = 5000;

interface TrackedComponent {
  id: number;
  name: string;
  finished: boolean;
  promise: Promise<void>;
  markFinished: () => void;
}

function resetTracked(entry: TrackedComponent): void {
  entry.finished = false;
  entry.promise = new Promise<void>((resolve) => {
    entry.markFinished = resolve;
  });
}

const IGNORED_REPORTER: LoadReporter = {
  reportLoaded() {},
  reportFailed() {},
  stopWaiting() {},
};

export function createPageLoadTracker(
  options: PageLoadTrackerOptions = {},
): PageLoadTracker {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const pageName = options.name ?? 'page';

  const state = ref<PageLoadState>('loading');
  const error = ref<unknown>(null);
  const retryCount = ref(0);
  const reloadsRunning = ref(0);

  const tracked = new Map<number, TrackedComponent>();
  let pageEntry: TrackedComponent | null = null;
  let nextId = 0;
  let runId = 0;
  let stopped = false;

  let finishedResolve: (() => void) | null = null;
  let finishedPromise = newFinishedPromise();

  function newFinishedPromise(): Promise<void> {
    return new Promise<void>((resolve) => {
      finishedResolve = resolve;
    });
  }

  function unfinished(): TrackedComponent[] {
    return [...tracked.values()].filter((entry) => !entry.finished);
  }

  function stillLoading(): string[] {
    return unfinished().map((entry) => entry.name);
  }

  function settle(next: PageLoadState): void {
    if (state.value !== 'loading') {
      return;
    }
    state.value = next;
    log('[%s] %s', pageName, next);
    finishedResolve?.();
    finishedResolve = null;
  }

  function reporterFor(entry: TrackedComponent): LoadReporter {
    const markFinished = () => {
      if (entry.finished) {
        return false;
      }
      entry.finished = true;
      entry.markFinished();
      return true;
    };

    return {
      reportLoaded() {
        markFinished();
      },
      reportFailed(err: unknown) {
        if (!markFinished()) {
          return;
        }
        if (state.value === 'loading') {
          log('[%s] %s failed: %o', pageName, entry.name, err);
          error.value = err;
          settle('failed');
        }
      },
      stopWaiting() {
        tracked.delete(entry.id);
        markFinished();
      },
    };
  }

  function waitFor(componentName: string): LoadReporter {
    if (state.value !== 'loading' || stopped) {
      log('[%s] ignoring late registration: %s', pageName, componentName);
      return IGNORED_REPORTER;
    }

    const id = nextId++;
    const entry = { id, name: componentName } as TrackedComponent;
    resetTracked(entry);
    tracked.set(id, entry);
    log(
      '[%s] waiting for %s (%d total)',
      pageName,
      componentName,
      tracked.size,
    );

    return reporterFor(entry);
  }

  function waitForAllOrTimeout(
    promises: Promise<void>[],
    deadline: number,
  ): Promise<boolean> {
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      return Promise.resolve(true);
    }
    return new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => resolve(true), remaining);
      void Promise.all(promises).then(() => {
        clearTimeout(timer);
        resolve(false);
      });
    });
  }

  async function track(): Promise<void> {
    const thisRun = ++runId;
    let deadline = Date.now() + timeoutMs;
    const stillTracking = () =>
      thisRun === runId && !stopped && state.value === 'loading';

    await nextTick();

    while (stillTracking()) {
      const waiting = unfinished();

      if (waiting.length === 0) {
        await nextTick();
        if (!stillTracking()) {
          return;
        }
        if (unfinished().length === 0) {
          settle('loaded');
        }
        continue;
      }

      if (pageEntry && !pageEntry.finished) {
        await pageEntry.promise;
        if (!stillTracking()) {
          return;
        }
        deadline = Date.now() + timeoutMs;
        await nextTick();
        continue;
      }

      const timedOut = await waitForAllOrTimeout(
        waiting.map((entry) => entry.promise),
        deadline,
      );
      if (!stillTracking()) {
        return;
      }
      if (timedOut) {
        console.warn(
          `[integro] ${pageName} gave up waiting after ${timeoutMs}ms and is showing anyway. ` +
            `Still loading: ${stillLoading().join(', ') || '(nothing)'}`,
        );
        settle('loaded');
        return;
      }
      await nextTick();
    }
  }

  function retry(): void {
    if (stopped || state.value === 'loading') {
      return;
    }
    for (const entry of tracked.values()) {
      resetTracked(entry);
    }
    error.value = null;
    state.value = 'loading';
    finishedPromise = newFinishedPromise();
    retryCount.value += 1;
    log('[%s] retry #%d', pageName, retryCount.value);
    void track();
  }

  function reloadStarted(): () => void {
    reloadsRunning.value += 1;
    let finished = false;
    return () => {
      if (!finished) {
        finished = true;
        reloadsRunning.value -= 1;
      }
    };
  }

  function stop(): void {
    stopped = true;
    runId += 1;
    tracked.clear();
    finishedResolve?.();
    finishedResolve = null;
  }

  const pageReporter = waitFor(pageName);
  pageEntry = tracked.values().next().value ?? null;
  void track();

  return {
    state: readonly(state),
    error: readonly(error) as Readonly<Ref<unknown>>,
    retryCount: readonly(retryCount),
    reloading: computed(() => reloadsRunning.value > 0),
    pageReporter,
    waitFor,
    reloadStarted,
    whenFinished: () => finishedPromise,
    retry,
    stillLoading,
    stop,
  };
}

export function usePageLoadTracker(
  options: PageLoadTrackerOptions = {},
): PageLoadTracker {
  const tracker = createPageLoadTracker(options);
  provide(PAGE_LOAD_TRACKER_KEY, tracker);
  if (getCurrentScope()) {
    onScopeDispose(() => tracker.stop());
  }
  return tracker;
}

export function injectPageLoadTracker(): PageLoadTracker | null {
  return inject<PageLoadTracker | null>(PAGE_LOAD_TRACKER_KEY, null);
}
